import { type ReactNode } from 'react';
import { Box, FormHelperText, Stack, Typography } from '@mui/material';
import type { ControllerRenderProps } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';

import { theme } from '@theguarantors/tg-package-material-ui';

import {
  GroupRadioButton,
  GroupRadioButtonSkeleton,
  type RadioInputBox,
} from '@/shared/components/input/GroupRadioButton';
import { createDataTestId } from '@/utils/createDataTestId';

import { fieldHelperTextSx } from '../../components/field-helper.text';
import type { OptionBoxOptionsSource } from '../../config';
import type { OptionBoxOption } from '../../lease-details.types';
import { useResolvedOptions } from '../../hooks';
import { useLeaseField } from '../../hooks/use-lease-field';
import { useLeaseDetailsUI } from '../../lease-details-ui.context';
import { useOptionBoxSelectionSync } from './use-option-box-selection-sync';

export type LeaseOptionBoxSelection = {
  /** The selected option's `value` (what lands in form state). */
  value: string;
  /** The selected option's `id` (distinguishes options that share a value). */
  optionId: string;
};

export type LeaseOptionBoxInputProps = {
  name: string;
  options: OptionBoxOptionsSource;
  /** Optional heading above the radios (e.g. "Rent Coverage"). */
  label?: ReactNode;
  /** Second form key that receives the selected option's `id`, if any. */
  companionIdField?: string;
  /** Form keys derived from the selected option; see `OptionBoxFieldConfig.companionFields`. */
  companionFields?: Record<string, (option: OptionBoxOption) => unknown>;
  /** Also clear a selection whose option became disabled. */
  clearDisabledSelection?: boolean;
  layout?: 'inline' | 'stacked';
  useDrawerStyles?: boolean;
  skeletonCount?: number;
  required?: boolean;
  disabled?: boolean;
  onSelect?: (selection: LeaseOptionBoxSelection) => void;
};

const toRadioInputBox = (option: OptionBoxOption): RadioInputBox => ({
  id: option.id,
  value: option.value,
  text: option.label,
  subText: option.subText,
  tooltip: option.tooltip,
  disabled: option.disabled,
  disabledTooltip: option.disabledTooltip,
});

// Card-style radio group bound to RHF via `useLeaseField`. Mirrors how the
// legacy invite/CLD radios wired `GroupRadioButton` inside a `Controller`, so
// the rendered output is identical — only the binding moves to the engine.
export function LeaseOptionBoxInput({
  name,
  options: optionsSource,
  label,
  companionIdField,
  companionFields,
  clearDisabledSelection = false,
  layout,
  useDrawerStyles,
  skeletonCount,
  required = false,
  disabled = false,
  onSelect,
}: LeaseOptionBoxInputProps) {
  const { setValue, control } = useFormContext();
  const boundField = useLeaseField<string | null | undefined>(name);
  const { options, isLoading, isError } = useResolvedOptions<OptionBoxOption>(optionsSource);
  const { asteriskColor, sectionSpacing } = useLeaseDetailsUI();
  const resolvedAsteriskColor = asteriskColor ?? theme.palette.red[600];
  const optionBoxSpacing = sectionSpacing?.optionBox ?? 1.5;

  const value = boundField.value ?? '';
  // `useWatch` with no name subscribes to the whole form, so pass a placeholder when unset.
  const companionId = useWatch({ control, name: companionIdField ?? '__no_companion_id__' }) as
    string | undefined;
  const selectedId = companionIdField ? companionId : undefined;
  const error = boundField.error;

  useOptionBoxSelectionSync({
    name,
    value,
    selectedId,
    options,
    isSettled: !isLoading && !isError,
    companionIdField,
    companionFields,
    clearDisabledSelection,
  });

  const dataTestId = createDataTestId({ inputString: name, suffix: 'option-box' });

  const labelNode = label != null && label !== '' && (
    <Typography
      variant="body2"
      sx={{
        color: theme.palette.neutralGrey[950],
        fontSize: '14px',
        fontWeight: 350,
        lineHeight: '22px',
        letterSpacing: '0',
      }}
    >
      {label}
      {required ? (
        <Box component="span" sx={{ color: resolvedAsteriskColor }}>
          {' '}
          *
        </Box>
      ) : null}
    </Typography>
  );

  if (isLoading) {
    return (
      <Stack direction="column" spacing={optionBoxSpacing}>
        {labelNode}
        <GroupRadioButtonSkeleton
          count={skeletonCount ?? (options.length || 3)}
          dataTestId={`${name}-skeleton`}
        />
      </Stack>
    );
  }

  if (isError) {
    return (
      <FormHelperText error={true} sx={fieldHelperTextSx} data-testid={`${dataTestId}-helper-text`}>
        Failed to load options. Please try again.
      </FormHelperText>
    );
  }

  // `GroupRadioButton` calls `onChange(event, value, id)`, so adapt the controller binding.
  const field = {
    name,
    value,
    ref: boundField.ref,
    onBlur: boundField.onBlur,
    onChange: (_event: unknown, selectedValue: string, selectedOptionId: string) => {
      if (companionIdField) {
        setValue(companionIdField, selectedOptionId, { shouldDirty: true });
      }
      const selected = options.find((option) => option.id === selectedOptionId);
      for (const [fieldName, derive] of Object.entries(companionFields ?? {})) {
        setValue(fieldName, selected ? derive(selected) : null, { shouldDirty: true });
      }
      boundField.onChange(selectedValue);
      onSelect?.({ value: selectedValue, optionId: selectedOptionId });
    },
  } as unknown as ControllerRenderProps;

  return (
    <Stack direction="column" spacing={optionBoxSpacing}>
      {labelNode}
      <Stack direction="column" spacing={optionBoxSpacing}>
        <GroupRadioButton
          id={name}
          dataTestId={dataTestId}
          options={options.map(toRadioInputBox)}
          selectedId={selectedId}
          field={field}
          required={required}
          disabled={disabled}
          layout={layout}
          useDrawerStyles={useDrawerStyles}
          styles={{
            options: {
              spacing: optionBoxSpacing,
              height: 64,
            },
          }}
        />
        {error?.message ? (
          <FormHelperText
            error={true}
            sx={fieldHelperTextSx}
            data-testid={`${dataTestId}-helper-text`}
          >
            {error.message.toString()}
          </FormHelperText>
        ) : null}
      </Stack>
    </Stack>
  );
}
